<template>
  <el-form
    v-model="account"
    :disabled="disabled"
    label-position="right"
    label-width="200px"
    @submit.prevent
  >
    <el-form-item v-if="!disabled">
      <el-alert
        :title="`Please provide ${systemTitle} account information`"
        type="warning"
      />
    </el-form-item>
    <el-form-item :error="errors.account_type" prop="account_type" label="Type">
      <TaxStatusSelect
        v-model="account.account_type"
        :disabled="disabledFields.account_type"
      />
    </el-form-item>
    <el-form-item :error="errors.account" prop="account" :label="accountTitle">
      <el-input
        v-model="account.account"
        :disabled="disabledFields.account"
        :placeholder="accountHint"
      />
    </el-form-item>
    <template v-if="account.account_type === ContactType.Personal">
      <el-form-item :error="errors.first_name" label="First Name">
        <el-input
          v-model="account.first_name"
          :disabled="disabledFields.first_name"
        />
      </el-form-item>
      <el-form-item :error="errors.last_name" label="Last Name">
        <el-input
          v-model="account.last_name"
          :disabled="disabledFields.last_name"
        />
      </el-form-item>
    </template>
    <el-form-item
      v-if="account.account_type === ContactType.Corporate"
      :error="errors.company_name"
      label="Company Name"
    >
      <el-input v-model="account.company_name" />
    </el-form-item>
    <el-form-item prop="country_code" :error="errors.country" label="Country">
      <CountrySelect
        v-model="account.country_code"
        :items="countries"
        :disabled="disabledFields.country_code"
      />
    </el-form-item>
    <el-form-item :error="errors.address" label="Address">
      <el-input v-model="account.address" :disabled="disabledFields.address" />
    </el-form-item>
    <el-form-item>
      <el-button
        type="primary"
        plain
        :loading="loading"
        :disabled="disabled && !verificationAllowed"
        @click.prevent="onSubmit"
      >
        {{ buttonText }}
      </el-button>
      <router-link
        to="/payment"
        class="el-button el-button--default el-button--medium"
      >
        Back to payments
      </router-link>
    </el-form-item>
  </el-form>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import { ContactType, PaymentValidationErrorsKey } from '@/api/types'
import useValidation from '@/hooks/useValidation'
import { useCountriesStore } from '@/store/modules/countries'
import TaxStatusSelect from '@/components/Select/TaxStatusSelect.vue'
import CountrySelect from '@/components/Select/CountrySelect.vue'
import { attachVerifySDK } from '@/utils/third-party'
import { safeSubmitProcessing } from '@/utils/helpers'
import { usePaymentProfilesStore } from '@/store/modules/paymentProfiles'
import {
  IPaymentProfileResponse,
  IPaymentProfileAttributes,
  PredefinedFields,
  SystemAccountTitle,
  SystemAccountHint
} from '@/interfaces/paymentProfiles'
import { finishItem as finishUserVerification } from '@/api/user_verification'

declare global {
  interface Window {
    veriffSDK: any
  }
}

export default defineComponent({
  name: 'PaymentProfileForm',
  components: {
    TaxStatusSelect,
    CountrySelect
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    sid: {
      type: String,
      required: true
    }
  },
  setup(props, context) {
    const paymentProfilesStore = usePaymentProfilesStore()
    const countriesStore = useCountriesStore()

    attachVerifySDK()

    const account = ref({ ...props.modelValue })

    countriesStore.GetItems()

    const { provideValidation, setValidationErrors } = useValidation(
      PaymentValidationErrorsKey
    )
    const errors = provideValidation()

    const FORBIDDEN_COUNTRY_ISO = ['in', 'pk', 'bd']

    const loading = ref(false)

    const countries = computed(() => {
      return countriesStore.sorted
    })

    const disabled = computed(() => {
      return account.value.verified || loading.value
    })

    const verificationAllowed = computed(() => {
      return (
        account.value.account_type === ContactType.Personal &&
        !FORBIDDEN_COUNTRY_ISO.includes(account.value.country_code)
      )
    })

    const buttonText = computed(() => {
      return verificationAllowed.value ? 'Check Identity document' : 'Save'
    })

    const handleModalEvent = (message: string) => {
      switch (message) {
        case window.veriffSDK.MESSAGES.CANCELED:
          window.location.reload()
          break
        case window.veriffSDK.MESSAGES.FINISHED:
          finishUserVerification().then(() => {
            window.location.reload()
          })
      }
    }

    const onSuccess = async (response: IPaymentProfileResponse) => {
      if (response.url) {
        window.veriffSDK.createVeriffFrame({
          url: response.url,
          onEvent: handleModalEvent
        })
      } else {
        window.location.reload()
      }
    }

    const onSubmit = async () => {
      await safeSubmitProcessing(
        loading,
        () => {
          return paymentProfilesStore.SaveItem(
            account.value as IPaymentProfileAttributes
          )
        },
        (response: IPaymentProfileResponse) => onSuccess(response),
        (err) => setValidationErrors(err, errors)
      )
    }

    const systemTitle = props.modelValue.title

    let disabledFields: { [name: string]: boolean } = {}

    PredefinedFields.forEach((name: string, index: number) => {
      disabledFields[name] =
        account.value[name].length > 0 && account.value.source_exist
    })

    const accountTitle = SystemAccountTitle[props.modelValue.sid]
    const accountHint = SystemAccountHint[props.modelValue.sid]

    return {
      onSubmit,
      errors,
      loading,
      disabled,
      countries,
      ContactType,
      buttonText,
      account,
      verificationAllowed,
      systemTitle,
      disabledFields,
      accountTitle,
      accountHint
    }
  }
})
</script>
